import api, { uploadApi } from './axios';

/**
 * Get all communities with pagination and filters
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.category - Filter by category
 * @param {string} options.search - Search term
 * @param {string} options.sort - Sort field and direction
 * @returns {Promise} Promise object representing the API response
 */
export const getCommunities = (options = {}) => {
  const {
    page = 1,
    limit = 10,
    category,
    search,
    joined = null,
    sort = '-createdAt',
  } = options;
  
  return api.get('/communities', {
    params: {
      page,
      limit,
      category,
      search,
      sort,
      joined
    },
  });
};

/**
 * Create a new community
 * @param {Object} communityData - Community data
 * @param {string} communityData.name - Community name
 * @param {string} communityData.description - Community description
 * @param {string} communityData.category - Community category
 * @param {boolean} communityData.isPrivate - Whether the community is private
 * @param {Array} communityData.rules - Community rules
 * @returns {Promise} Promise object representing the API response
 */
export const createCommunity = (communityData) => {
  return api.post('/communities', communityData);
};

/**
 * Get a community by ID or slug
 * @param {string} idOrSlug - Community ID or slug
 * @returns {Promise} Promise object representing the API response
 */
export const getCommunityByIdOrSlug = (idOrSlug) => {
  return api.get(`/communities/${idOrSlug}`);
};

/**
 * Update a community
 * @param {string} communityId - Community ID
 * @param {Object} communityData - Updated community data
 * @returns {Promise} Promise object representing the API response
 */
export const updateCommunity = (communityId, communityData) => {
  return api.put(`/communities/${communityId}`, communityData);
};

/**
 * Upload a community profile picture
 * @param {string} communityId - Community ID
 * @param {File} profileImage - Profile image file
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const uploadCommunityProfilePicture = (
  communityId,
  profileImage,
  onUploadProgress
) => {
  const formData = new FormData();
  formData.append('profileImage', profileImage);
  
  return api.put(
    `/communities/${communityId}/profile-picture`,
    formData,
    {headers: {
      'Content-Type': 'multipart/form-data'
    }},
    onUploadProgress
  );
};

/**
 * Upload a community cover picture
 * @param {string} communityId - Community ID
 * @param {File} coverImage - Cover image file
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const uploadCommunityCoverPicture = (
  communityId,
  coverImage,
  onUploadProgress
) => {
  const formData = new FormData();
  formData.append('coverImage', coverImage);
  
  return api.put(
    `/communities/${communityId}/cover-picture`,
    formData,
    {headers: {
      'Content-Type': 'multipart/form-data'
    }},
    onUploadProgress
  );
};

/**
 * Join a community
 * @param {string} communityId - Community ID
 * @returns {Promise} Promise object representing the API response
 */
export const joinCommunity = (communityId) => {
  return api.put(`/communities/${communityId}/join`);
};

/**
 * Leave a community
 * @param {string} communityId - Community ID
 * @returns {Promise} Promise object representing the API response
 */
export const leaveCommunity = (communityId) => {
  return api.delete(`/communities/${communityId}/join`);
};

/**
 * Get community members
 * @param {string} communityId - Community ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getCommunityMembers = (communityId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/communities/${communityId}/members`, {
    params: {
      page,
      limit,
    },
  });
};

/**
 * Get communities the current user is a member of
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getUserCommunities = (options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get('/communities/user', {
    params: {
      page,
      limit,
    },
  });
};

/**
 * Get community updates
 * @param {string} communityId - Community ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getCommunityUpdates = (communityId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/communities/${communityId}/updates`, {
    params: {
      page,
      limit,
    },
  });
};
 

/**
 * Create a community update
 * @param {string} communityId - Community ID
 * @param {Object} updateData - Update data (title, content, type)
 * @returns {Promise} Promise object representing the API response
 */
export const createCommunityUpdate = (communityId, updateData) => {
  return api.post(`/communities/${communityId}/updates`, updateData);
};

/**
 * Update a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @param {Object} updateData - Updated data
 * @returns {Promise} Promise object representing the API response
 */
export const updateCommunityUpdate = (communityId, updateId, updateData) => {
  return api.put(`/communities/${communityId}/updates/${updateId}`, updateData);
};

/**
 * Delete a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const deleteCommunityUpdate = (communityId, updateId) => {
  return api.delete(`/communities/${communityId}/updates/${updateId}`);
};

/**
 * Pin a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const pinCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/pin/${updateId}`);
};

/**
 * Unpin a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const unpinCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/unpin/${updateId}`);
};

/**
 * Like a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const likeCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/updates/${updateId}/like`);
};

/**
 * Unlike a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const unlikeCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/updates/${updateId}/unlike`);
};

/**
 * Save a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const saveCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/updates/${updateId}/save`);
};

/**
 * Unsave a community update
 * @param {string} communityId - Community ID
 * @param {string} updateId - Update ID
 * @returns {Promise} Promise object representing the API response
 */
export const unsaveCommunityUpdate = (communityId, updateId) => {
  return api.put(`/communities/${communityId}/updates/${updateId}/unsave`);
};


/**
 * Create or update a community page
 * @param {string} communityId - Community ID
 * @param {Object} pageData - Page content and settings
 * @returns {Promise} Promise object representing the API response
 */
export const updateCommunityPage = (communityId, pageData) => {
  return api.put(`/communities/${communityId}/page`, pageData);
};

/**
 * Get a community page
 * @param {string} communityId - Community ID
 * @returns {Promise} Promise object representing the API response
 */
export const getCommunityPage = (communityId) => {
  return api.get(`/communities/${communityId}/page`);
};
